const STORAGE_KEY = 'heureka.session';

export interface StoredSession {
  token: string;
  expiresAt: string;
  username: string;
}

/**
 * The token lives in localStorage so a reload does not sign the operator out.
 *
 * That does expose it to any script running on this origin, which an httpOnly
 * cookie would not — the trade is deliberate: a cookie would need the API and
 * the dashboard to stay same-site, and this app is meant to be reachable from a
 * laptop against an API on another host. The token is short-lived and grants
 * read access to one shop's pricing, not to money or to the shop itself.
 */
export function readSession(): StoredSession | null {
  const raw = localStorage.getItem(STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredSession;

    if (!parsed.token || !parsed.expiresAt) {
      return null;
    }

    // A token past its expiry is worth nothing; drop it before any request is
    // built with it, so the operator sees the sign-in screen and not an error.
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);

      return null;
    }

    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);

    return null;
  }
}

export function writeSession(session: StoredSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}
